/**
 * InstantAlignment — Eyes-closed, audio-led alignment experience
 *
 * Flow: Entry → Input → Generating (breath) → Playing → Save prompt
 * Distinct from Immersion: no visuals, no setup, instant access
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Volume2, VolumeX, ArrowLeft } from 'lucide-react';
import { voiceVolume, getStoredAudioMode } from '../utils/modeAudio';

const API = process.env.REACT_APP_BACKEND_URL;

const EXAMPLES = [
  'Calm confidence',
  'My best golf',
  'Mega abundance',
  'Presence',
  'Deep connection',
];

// ─── Entry Screen ─────────────────────────────────
const EntryScreen = ({ onBegin }) => {
  const [breathScale, setBreathScale] = useState(1);
  const [breathCycle, setBreathCycle] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const cycle = () => {
      if (cancelled) return;
      setBreathScale(1.08);
      setTimeout(() => {
        if (cancelled) return;
        setBreathScale(1);
        setBreathCycle(c => c + 1);
        setTimeout(() => { if (!cancelled) cycle(); }, 3000);
      }, 4000);
    };
    const t = setTimeout(cycle, 2000);
    return () => { cancelled = true; clearTimeout(t); };
  }, []);

  return (
    <motion.div
      className="fixed inset-0 flex flex-col items-center justify-center z-10"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 2 }}
      data-testid="alignment-entry"
    >
      {/* Soft radial glow */}
      <motion.div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 300, height: 300,
          background: 'radial-gradient(circle, rgba(210,175,130,0.06) 0%, rgba(210,175,130,0.02) 40%, transparent 70%)',
          filter: 'blur(40px)',
        }}
        animate={{ scale: breathScale }}
        transition={{ duration: breathScale > 1 ? 4 : 3, ease: 'easeInOut' }}
      />

      <motion.p
        className="text-white/40 text-lg text-center px-12 leading-loose mb-2"
        style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic' }}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 2 }}
      >
        There is nothing to figure out…
      </motion.p>

      <motion.p
        className="text-white/50 text-xl text-center px-12 leading-loose"
        style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic' }}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 3, duration: 2 }}
      >
        Just step into it
      </motion.p>

      <motion.button
        className="mt-16 text-white/30 text-base tracking-widest cursor-pointer bg-transparent border-none outline-none"
        style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.4, 0.2, 0.4] }}
        transition={{ delay: 5, duration: 4, repeat: Infinity }}
        onClick={onBegin}
        data-testid="alignment-begin-btn"
      >
        Begin
      </motion.button>
    </motion.div>
  );
};

// ─── Input Screen ─────────────────────────────────
const InputScreen = ({ onSubmit }) => {
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 800);
    return () => clearTimeout(t);
  }, []);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (value.trim()) onSubmit(value.trim());
  };

  const handleExample = (ex) => {
    setValue(ex);
    setTimeout(() => onSubmit(ex), 300);
  };

  return (
    <motion.div
      className="fixed inset-0 flex flex-col items-center justify-center z-10 px-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.5 }}
      data-testid="alignment-input"
    >
      <motion.p
        className="text-white/35 text-base mb-10 tracking-wide"
        style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic' }}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 1.5 }}
      >
        What are you stepping into?
      </motion.p>

      <form onSubmit={handleSubmit} className="w-full max-w-md">
        <motion.input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Calm confidence…"
          className="w-full bg-transparent border-none outline-none text-center text-white/70 text-2xl placeholder-white/15 pb-3"
          style={{
            fontFamily: "'Spectral', serif",
            fontStyle: 'italic',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            caretColor: 'rgba(210,175,130,0.4)',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 1 }}
          data-testid="alignment-intention-input"
          autoComplete="off"
        />
      </form>

      {/* Example chips */}
      <motion.div
        className="flex flex-wrap justify-center gap-3 mt-10 max-w-lg"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 1.5 }}
      >
        {EXAMPLES.map((ex) => (
          <button
            key={ex}
            onClick={() => handleExample(ex)}
            className="px-4 py-1.5 rounded-full text-white/20 text-xs tracking-wide cursor-pointer transition-all duration-500 hover:text-white/40 hover:bg-white/[0.03]"
            style={{
              fontFamily: "'Manrope', sans-serif",
              border: '1px solid rgba(255,255,255,0.06)',
              background: 'rgba(255,255,255,0.02)',
            }}
            data-testid={`alignment-example-${ex.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {ex}
          </button>
        ))}
      </motion.div>

      {/* Submit hint */}
      <AnimatePresence>
        {value.trim().length > 0 && (
          <motion.button
            onClick={handleSubmit}
            className="mt-10 text-white/25 text-sm tracking-widest cursor-pointer bg-transparent border-none outline-none"
            style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0.2, 0.4, 0.2] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 3, repeat: Infinity }}
            data-testid="alignment-submit-btn"
          >
            Step in
          </motion.button>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ─── Generating Screen (breath animation while AI + TTS work) ───
const GeneratingScreen = ({ intention }) => {
  const [breathScale, setBreathScale] = useState(1);

  useEffect(() => {
    let cancelled = false;
    const cycle = () => {
      if (cancelled) return;
      setBreathScale(1.2);
      setTimeout(() => {
        if (cancelled) return;
        setBreathScale(1);
        setTimeout(() => { if (!cancelled) cycle(); }, 2500);
      }, 3500);
    };
    cycle();
    return () => { cancelled = true; };
  }, []);

  return (
    <motion.div
      className="fixed inset-0 flex flex-col items-center justify-center z-10 pointer-events-none"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.5 }}
      data-testid="alignment-generating"
    >
      <motion.div
        className="rounded-full mb-12"
        style={{
          width: 100, height: 100,
          background: 'radial-gradient(circle, rgba(210,175,130,0.08) 0%, rgba(210,175,130,0.02) 50%, transparent 70%)',
          border: '1px solid rgba(210,175,130,0.06)',
        }}
        animate={{ scale: breathScale }}
        transition={{ duration: breathScale > 1 ? 3.5 : 2.5, ease: 'easeInOut' }}
      />

      <motion.p
        className="text-white/25 text-sm tracking-widest"
        style={{ fontFamily: "'Manrope', sans-serif" }}
        animate={{ opacity: [0.15, 0.3, 0.15] }}
        transition={{ duration: 4, repeat: Infinity }}
      >
        {intention}
      </motion.p>
    </motion.div>
  );
};

// ─── Playing Screen ──────────────────────────────
const PlayingScreen = ({ audioUrl, intention, muted, onToggleMute, loopCount, onSavePrompt }) => {
  const [breathScale, setBreathScale] = useState(1);

  useEffect(() => {
    let cancelled = false;
    const cycle = () => {
      if (cancelled) return;
      setBreathScale(1.06);
      setTimeout(() => {
        if (cancelled) return;
        setBreathScale(1);
        setTimeout(() => { if (!cancelled) cycle(); }, 4000);
      }, 5000);
    };
    const t = setTimeout(cycle, 1000);
    return () => { cancelled = true; clearTimeout(t); };
  }, []);

  return (
    <motion.div
      className="fixed inset-0 flex flex-col items-center justify-center z-10"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 2 }}
      data-testid="alignment-playing"
    >
      {/* Very subtle ambient glow */}
      <motion.div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 400, height: 400,
          background: 'radial-gradient(circle, rgba(210,175,130,0.04) 0%, rgba(210,175,130,0.01) 50%, transparent 70%)',
          filter: 'blur(60px)',
        }}
        animate={{ scale: breathScale }}
        transition={{ duration: breathScale > 1 ? 5 : 4, ease: 'easeInOut' }}
      />

      {/* Eyes closed prompt — fades out after 8s */}
      <motion.p
        className="text-white/25 text-xs tracking-[0.25em] uppercase mb-8"
        style={{ fontFamily: "'Manrope', sans-serif" }}
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.25, 0.25, 0] }}
        transition={{ duration: 8, times: [0, 0.15, 0.7, 1] }}
      >
        Close your eyes
      </motion.p>

      {/* Intention text — very subtle */}
      <motion.p
        className="text-white/15 text-sm text-center"
        style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic' }}
        animate={{ opacity: [0.1, 0.2, 0.1] }}
        transition={{ duration: 8, repeat: Infinity }}
      >
        {intention}
      </motion.p>

      {/* Mute control — bottom */}
      <div className="fixed bottom-8 right-8 z-20">
        <button
          onClick={onToggleMute}
          className="p-3 text-white/15 transition-colors duration-300 hover:text-white/30"
          data-testid="alignment-mute-btn"
        >
          {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>
      </div>
    </motion.div>
  );
};

// ─── Save Prompt Screen ───────────────────────────
const SavePromptScreen = ({ intention, onSave, onContinue, onDeeper }) => {
  const [name, setName] = useState(intention);

  return (
    <motion.div
      className="fixed inset-0 flex flex-col items-center justify-center z-10 px-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 2 }}
      data-testid="alignment-save-prompt"
    >
      <motion.p
        className="text-white/40 text-lg text-center mb-10"
        style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic' }}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 1.5 }}
      >
        Would you like to keep this?
      </motion.p>

      <motion.input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="w-full max-w-sm bg-transparent border-none outline-none text-center text-white/50 text-lg pb-2 mb-10"
        style={{
          fontFamily: "'Spectral', serif",
          fontStyle: 'italic',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 1 }}
        data-testid="alignment-save-name-input"
      />

      <motion.div
        className="flex flex-col items-center gap-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1.5 }}
      >
        <button
          onClick={() => onSave(name)}
          className="text-white/40 text-sm tracking-widest cursor-pointer bg-transparent border-none outline-none transition-colors duration-300 hover:text-white/60"
          style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic' }}
          data-testid="alignment-save-btn"
        >
          Save
        </button>

        <button
          onClick={onContinue}
          className="text-white/20 text-xs tracking-widest cursor-pointer bg-transparent border-none outline-none transition-colors duration-300 hover:text-white/35"
          style={{ fontFamily: "'Manrope', sans-serif" }}
          data-testid="alignment-continue-btn"
        >
          Keep listening
        </button>

        <button
          onClick={onDeeper}
          className="text-white/15 text-xs tracking-wide cursor-pointer bg-transparent border-none outline-none mt-4 transition-colors duration-300 hover:text-white/30"
          style={{ fontFamily: "'Manrope', sans-serif" }}
          data-testid="alignment-go-deeper-btn"
        >
          Go deeper
        </button>
      </motion.div>
    </motion.div>
  );
};

// ─── Saved Confirmation Screen ────────────────────
const SavedScreen = ({ name, onDeeper, onDone }) => (
  <motion.div
    className="fixed inset-0 flex flex-col items-center justify-center z-10"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 2 }}
    data-testid="alignment-saved"
  >
    <motion.p
      className="text-white/35 text-base mb-3"
      style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic' }}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5, duration: 1.5 }}
    >
      Saved
    </motion.p>
    <motion.p
      className="text-white/50 text-xl mb-12"
      style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic' }}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1, duration: 1.5 }}
    >
      {name}
    </motion.p>

    <motion.div
      className="flex flex-col items-center gap-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 2, duration: 1.5 }}
    >
      <button
        onClick={onDeeper}
        className="text-white/25 text-xs tracking-widest cursor-pointer bg-transparent border-none outline-none transition-colors duration-300 hover:text-white/40"
        style={{ fontFamily: "'Manrope', sans-serif" }}
        data-testid="alignment-saved-deeper-btn"
      >
        Go deeper
      </button>
      <button
        onClick={onDone}
        className="text-white/15 text-xs tracking-wide cursor-pointer bg-transparent border-none outline-none transition-colors duration-300 hover:text-white/25"
        style={{ fontFamily: "'Manrope', sans-serif" }}
        data-testid="alignment-done-btn"
      >
        Return
      </button>
    </motion.div>
  </motion.div>
);

// ═══════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════

export default function InstantAlignment() {
  const navigate = useNavigate();
  const [screen, setScreen] = useState('entry');
  const [intention, setIntention] = useState('');
  const [sessionData, setSessionData] = useState(null);
  const [muted, setMuted] = useState(false);
  const [loopCount, setLoopCount] = useState(0);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const audioRef = useRef(null);
  const loopCountRef = useRef(0);

  const token = localStorage.getItem('reclaim_token');

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handleBegin = useCallback(() => {
    setScreen('input');
  }, []);

  const handleSubmit = useCallback(async (text) => {
    setIntention(text);
    setScreen('generating');

    try {
      const res = await fetch(`${API}/api/instant-alignment/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ intention: text }),
      });

      if (!res.ok) throw new Error('Generation failed');

      const data = await res.json();
      setSessionData(data);

      if (data.audio_url) {
        // Create audio element
        const audio = new Audio(`${API}${data.audio_url}`);
        audio.volume = 0;
        audio.loop = true;
        audioRef.current = audio;

        audio.addEventListener('timeupdate', () => {
          // Track loops by checking when audio restarts
          if (audio.currentTime < 1 && loopCountRef.current > 0) {
            const newCount = loopCountRef.current + 1;
            loopCountRef.current = newCount;
            setLoopCount(newCount);
            // After 2 loops, show save prompt
            if (newCount >= 2 && !showSavePrompt) {
              setShowSavePrompt(true);
            }
          }
          if (audio.currentTime > 2 && loopCountRef.current === 0) {
            loopCountRef.current = 1;
            setLoopCount(1);
          }
        });

        // Start playback with fade in
        audio.play().then(() => {
          let vol = 0;
          const voiceTarget = voiceVolume(getStoredAudioMode());
          const fade = setInterval(() => {
            vol += 0.008;
            if (vol >= voiceTarget || !audioRef.current) {
              clearInterval(fade);
              if (audioRef.current) audioRef.current.volume = voiceTarget;
            } else if (audioRef.current) {
              audioRef.current.volume = vol;
            }
          }, 50);
        }).catch(() => {});

        setScreen('playing');
      } else {
        // TTS failed but we have the script — still show playing with no audio
        setScreen('playing');
      }
    } catch (err) {
      console.error('Alignment generation error:', err);
      // Fallback: go back to input
      setScreen('input');
    }
  }, [token, showSavePrompt]);

  // Sync mute state
  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
  }, [muted]);

  const handleSave = useCallback(async (name) => {
    if (!sessionData) return;
    try {
      await fetch(`${API}/api/instant-alignment/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ session_id: sessionData.session_id, name }),
      });
    } catch (e) {}

    // Fade out audio
    if (audioRef.current) {
      let vol = audioRef.current.volume;
      const fade = setInterval(() => {
        vol -= 0.02;
        if (vol <= 0 || !audioRef.current) {
          clearInterval(fade);
          if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
        } else if (audioRef.current) {
          audioRef.current.volume = vol;
        }
      }, 50);
    }

    setScreen('saved');
    setSessionData(prev => prev ? { ...prev, saved_name: name } : prev);
  }, [sessionData, token]);

  const handleContinue = useCallback(() => {
    setShowSavePrompt(false);
    setScreen('playing');
  }, []);

  const handleGoDeeper = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    navigate('/healing');
  }, [navigate]);

  const handleDone = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    navigate('/');
  }, [navigate]);

  const handleBack = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    navigate(-1);
  }, [navigate]);

  return (
    <div className="fixed inset-0 overflow-hidden" style={{ background: 'rgba(10,7,4,1)' }} data-testid="instant-alignment-page">
      {/* Ambient background gradient */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 50% 45%, rgba(25,18,10,1) 0%, rgba(10,7,4,1) 70%)',
        }}
      />

      {/* Back button — only on entry/input */}
      {(screen === 'entry' || screen === 'input') && (
        <motion.button
          className="fixed top-6 left-6 z-30 p-2 text-white/15 transition-colors duration-300 hover:text-white/30"
          onClick={handleBack}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 1 }}
          data-testid="alignment-back-btn"
        >
          <ArrowLeft size={20} />
        </motion.button>
      )}

      <AnimatePresence mode="wait">
        {screen === 'entry' && (
          <EntryScreen key="entry" onBegin={handleBegin} />
        )}
        {screen === 'input' && (
          <InputScreen key="input" onSubmit={handleSubmit} />
        )}
        {screen === 'generating' && (
          <GeneratingScreen key="generating" intention={intention} />
        )}
        {screen === 'playing' && !showSavePrompt && (
          <PlayingScreen
            key="playing"
            audioUrl={sessionData?.audio_url}
            intention={intention}
            muted={muted}
            onToggleMute={() => setMuted(m => !m)}
            loopCount={loopCount}
          />
        )}
        {screen === 'playing' && showSavePrompt && (
          <SavePromptScreen
            key="save-prompt"
            intention={intention}
            onSave={handleSave}
            onContinue={handleContinue}
            onDeeper={handleGoDeeper}
          />
        )}
        {screen === 'saved' && (
          <SavedScreen
            key="saved"
            name={sessionData?.saved_name || intention}
            onDeeper={handleGoDeeper}
            onDone={handleDone}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
